/**
 * Consentimiento de cookies (CMP) — vanilla-cookieconsent v3.
 *
 * Misma configuración que la app Nuxt (plugins/cookieconsent.client.ts en el
 * repo reserva-ya): mismo nombre de cookie `cc_cookie`, misma `revision` y las
 * mismas categorías. Landing y app comparten origen, así que la decisión que el
 * usuario toma aquí vale también en /login, en el alta y en el panel: no se le
 * vuelve a preguntar al cruzar de un sitio al otro.
 *
 * Reglas que hay que respetar al tocar esto (espec. AEPD/RGPD/LSSI):
 * - `mode: 'opt-in'`: nada que no sea necesario se ejecuta sin decisión previa.
 * - Aceptar y Rechazar con la misma visibilidad en la primera capa.
 * - Retirar el consentimiento tiene que ser tan fácil como darlo → el enlace
 *   «Configurar cookies» vive de forma permanente en el pie.
 * - Un servicio nuevo no necesario se carga SOLO dentro de onConsent/onChange
 *   comprobando `acceptedCategory`. Nunca cargar y desactivar después.
 * - Si cambia la finalidad, el tercero o los datos: subir `revision` para forzar
 *   una nueva solicitud, y actualizar la política de cookies.
 */
import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';

export const POLICY_VERSION = '2026-07-22';

/** ¿Ha aceptado el usuario la categoría de analítica? */
export const analyticsAllowed = (): boolean => {
  try {
    return CookieConsent.acceptedCategory('analytics');
  } catch {
    return false;
  }
};

interface CookieConsentBannerProps {
  /** Se invoca cuando cambia el consentimiento, con el estado de la analítica. */
  onConsentChange?: (analytics: boolean) => void;
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  onConsentChange,
}) => {
  useEffect(() => {
    const notify = () => onConsentChange?.(analyticsAllowed());

    CookieConsent.run({
      mode: 'opt-in',
      revision: 1,
      autoShow: true,
      disablePageInteraction: false,
      hideFromBots: true,

      cookie: {
        name: 'cc_cookie',
        expiresAfterDays: 365,
        sameSite: 'Lax',
      },

      guiOptions: {
        consentModal: {
          layout: 'box wide',
          position: 'bottom left',
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: 'box',
          equalWeightButtons: true,
          flipButtons: false,
        },
      },

      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: {
          enabled: false,
          readOnly: false,
          autoClear: {
            cookies: [{ name: /^(_ga|_gid|_gat|_fbp|_fbc)/ }],
            reloadPage: false,
          },
        },
      },

      language: {
        default: 'es',
        translations: {
          es: {
            consentModal: {
              title: 'Uso de cookies',
              description:
                'Utilizamos cookies y tecnologías similares propias, técnicas y necesarias, para que el sitio y la plataforma funcionen (sesión, seguridad, pagos y recordar tu decisión). Las de analítica y medición solo se activan si las aceptas. Consulta la <a href="/cookies">Política de cookies</a>.',
              acceptAllBtn: 'Aceptar todas',
              acceptNecessaryBtn: 'Rechazar todas',
              showPreferencesBtn: 'Configurar cookies',
            },
            preferencesModal: {
              title: 'Preferencias de cookies',
              acceptAllBtn: 'Aceptar todas',
              acceptNecessaryBtn: 'Rechazar todas',
              savePreferencesBtn: 'Guardar selección',
              closeIconLabel: 'Cerrar sin guardar',
              sections: [
                {
                  title: 'Tu privacidad, tu decisión',
                  description: `Aquí puedes activar o desactivar cada finalidad. Tu decisión se guarda durante 12 meses y puedes cambiarla en cualquier momento desde el enlace «Configurar cookies» del pie de página. Versión de la política: ${POLICY_VERSION}.`,
                },
                {
                  title:
                    'Cookies estrictamente necesarias <span class="pm__badge">Siempre activas</span>',
                  description:
                    'Imprescindibles para prestar el servicio que solicitas: mantener tu sesión iniciada, procesar pagos de forma segura, servir la aplicación y recordar tu decisión sobre cookies. No se utilizan para otros fines.',
                  linkedCategory: 'necessary',
                },
                {
                  title: 'Analítica y medición',
                  description:
                    'Nos permiten medir el uso del sitio para mejorarlo. Incluyen, cuando están configuradas, Google Tag Manager, Google Analytics 4 y Meta Pixel. No se cargan si no aceptas esta categoría.',
                  linkedCategory: 'analytics',
                },
                {
                  title: 'Más información',
                  description:
                    'Encontrarás el detalle completo (titular, duración, transferencias y cómo eliminar cookies desde tu navegador) en la <a href="/cookies">Política de cookies</a> y en la <a href="/politica-privacidad">Política de privacidad</a>.',
                },
              ],
            },
          },
        },
      },

      onConsent: notify,
      onChange: notify,
    });
  }, [onConsentChange]);

  return null;
};

export default CookieConsentBanner;
